import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MarketTrade,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMarketTradeColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterTableSort,
  type Column,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, GridOptions } from 'ag-grid-community';
import { useMemo } from 'react';
import { useMarketTradeMenu } from './MarketTradeMenu';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useMarketTradeFilter,
  type MarketTradeFilter,
} from './useMarketTradeFilter';

export interface MarketTradesBlotterTableProps {
  blotterID: string;
  tabLabel?: string;
  parentOrderID?: string;
  parentRFQID?: string;

  defaultColumns?: (Column | keyof MarketTrade)[];
  defaultFilter?: MarketTradeFilter;
  defaultSort?: BlotterTableSort<MarketTrade>;
  persistFilter?: boolean;
  persistSort?: boolean;

  initialIsOpen?: boolean;

  onCloneTab?: () => void;
  onRowDoubleClicked?: NonNullable<GridOptions['onRowDoubleClicked']>;
}

export function MarketTradesBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  defaultSort = '-TransactTime',
  persistFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: MarketTradesBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useMarketTradeColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter }, { persistFilter });

  const filterResults = useMarketTradeFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const marketTradeMenu = useMarketTradeMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(() => [...columns, ...marketTradeMenu.columns], [columns, marketTradeMenu.columns]);

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: 'MarketTrade',
      sort_by: '-TransactTime',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: MarketTrade.rowID,
    persistence: persistedBlotterTable,
    clientLocalFilter,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    contextMenuOptions: {
      getCustomContextMenuItems: marketTradeMenu.getCustomContextMenuItems,
    },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'MarketTrades',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      <BlotterTable {...blotterTable} />
      {marketTradeMenu.dialogs}
    </>
  );
}
